import { createFileRoute } from '@tanstack/react-router';
import { LayoutGridIcon } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { useCreateProject, useGetProjects } from '@/api/projects/queries';
import type { CreateProjectDto, Project } from '@/api/projects/types';
import { useGetSubscription } from '@/api/subscription/queries';
import { EmptyPlaceholder } from '@/components/empty-placeholder';
import { PageContainer } from '@/components/layout/page-container';
import { PageHeader } from '@/components/layout/page-header';
import CreateProjectDialog from './-components/create-project-dialog';
import { ProjectItem } from './-components/project-item';
import ProjectSetupDialog from './-components/project-setup-dialog';
import { UpgradePrompt } from './-components/upgrade-prompt';

export const Route = createFileRoute('/_authenticated/_global/projects/')({
  component: ProjectsComponent,
});

function ProjectsComponent() {
  const { data: projects, isPending: isProjectsPending } = useGetProjects();
  const { data: subscription, isPending: isSubscriptionPending } = useGetSubscription();
  const mutation = useCreateProject();
  const [createdProject, setCreatedProject] = useState<null | Project>(null);
  const [isSetupDialogOpen, setIsSetupDialogOpen] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const onSubmit = async (data: CreateProjectDto) => {
    return mutation.mutateAsync(data, {
      onSuccess: (data) => {
        setCreatedProject(data);
        setIsSetupDialogOpen(true);
        setIsDialogOpen(false);
        toast.success('Project has been created');
      },
    });
  };

  const isPending = isProjectsPending || isSubscriptionPending;
  const allowProjectCreation =
    projects && subscription && projects?.length < subscription?.allowedProjectsCount;

  return (
    <PageContainer>
      <PageHeader
        title="Projects"
        description="Manage your projects"
        rightControls={
          isPending ? null : (
            <div>
              {allowProjectCreation ? (
                <CreateProjectDialog
                  onSubmit={onSubmit}
                  isOpen={isDialogOpen}
                  setIsOpen={setIsDialogOpen}
                />
              ) : (
                <UpgradePrompt />
              )}
            </div>
          )
        }
      />
      <div className="mt-6 grid gap-6 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3">
        {!isProjectsPending && projects?.length
          ? projects.map((project) => {
              return <ProjectItem key={project.id} project={project} />;
            })
          : null}

        {!isProjectsPending && !projects?.length ? (
          <EmptyPlaceholder
            className="col-span-3"
            Icon={LayoutGridIcon}
            title="There are no projects yet"
            description="Create your first project to get started"
          />
        ) : null}
      </div>
      <ProjectSetupDialog
        project={createdProject}
        isOpen={isSetupDialogOpen}
        setIsOpen={setIsSetupDialogOpen}
      />
    </PageContainer>
  );
}
